import React, { useState, useEffect } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import {
    ChevronLeft,
    Truck,
    Wrench,
    Hammer,
    Monitor,
    Smartphone,
    TrendingUp,
    AlertCircle,
    DollarSign,
    Calendar,
    Upload,
    Package,
    ShieldCheck,
    Box,
    Building2,
    User,
    CheckCircle2,
    Info,
    Trash2,
    Plus,
    Anchor,
    Armchair,
    LayoutGrid,
    Save,
    ImageIcon
} from 'lucide-react';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Textarea } from '../../components/ui/textarea';
import { Badge } from '../../components/ui/badge';
import { cn } from '../../components/ui/utils';
import {
    Select,
    SelectContent,
    SelectItem,
    SelectTrigger,
    SelectValue,
} from "../../components/ui/select";
import { Switch } from '../../components/ui/switch';
import { toast } from 'sonner';
import api from '../../utils/api';
import { Asset, AssetType, AssetStatus, AssetCondition, AssetCustomField } from '../../types/asset';
import { useAbility } from "@casl/react";
import { AbilityContext } from "../../context/casl/AbilityContext";
import { ACTIONS, RESOURCES } from "../../constants/permissions.constants";
import AccessRestricted from '../../components/AccessRestricted';

const ASSET_TYPES = [
    { id: 'Vehicle', label: 'Vehicles', icon: <Truck size={20} />, color: 'text-blue-600', bg: 'bg-blue-50' },
    { id: 'Plant & Equipment', label: 'Plant & Equipment', icon: <Wrench size={20} />, color: 'text-emerald-600', bg: 'bg-emerald-50' },
    { id: 'Tools', label: 'Tools', icon: <Hammer size={20} />, color: 'text-orange-600', bg: 'bg-orange-50' },
    { id: 'Lifting/Slings', label: 'Lifting/Slings', icon: <Anchor size={20} />, color: 'text-amber-600', bg: 'bg-amber-50' },
    { id: 'IT Equipment', label: 'IT Equipment', icon: <Monitor size={20} />, color: 'text-indigo-600', bg: 'bg-indigo-50' },
    { id: 'Mobile Devices', label: 'Mobile Devices', icon: <Smartphone size={20} />, color: 'text-pink-600', bg: 'bg-pink-50' },
    { id: 'Furniture', label: 'Furniture', icon: <Armchair size={20} />, color: 'text-teal-600', bg: 'bg-teal-50' },
];


const CONDITION_OPTIONS = [
    'New',
    'Excellent',
    'Good',
    'Fair',
    'Poor',
    'Damaged'
];

const EditAsset: React.FC = () => {
    const { id } = useParams<{ id: string }>();
    const ability = useAbility(AbilityContext);
    const navigate = useNavigate();

    const [loading, setLoading] = useState(true);
    const [saving, setSaving] = useState(false);

    // Form State
    const [formData, setFormData] = useState({
        name: '',
        tag: '',
        type: '' as AssetType | '',
        brand: '',
        model: '',
        serialNumber: '',
        description: '',
        purchaseDate: '',
        registrationDate: '',
        purchasePrice: '',
        warrantyExpiryDate: '',
        status: 'Available' as AssetStatus,
        condition: 'Good' as AssetCondition,
        location: '',
        inspectionCadenceNumber: '0',
        inspectionCadenceUnit: 'days' as 'days' | 'weeks' | 'months',
        addToElectricalRegister: false,
        electricalCadenceNumber: '',
        electricalCadenceUnit: 'months' as 'days' | 'weeks' | 'months',
        addToLiftingRegister: false,
        liftingCadenceNumber: '',
        liftingCadenceUnit: 'months' as 'days' | 'weeks' | 'months',
        organizationId: '',
    });

    const [organizations, setOrganizations] = useState<any[]>([]);

    // Image state
    const [currentImage, setCurrentImage] = useState<string | null>(null);
    const [newImageFile, setNewImageFile] = useState<File | null>(null);
    const [imagePreview, setImagePreview] = useState<string | null>(null);
    const [imageRemoved, setImageRemoved] = useState(false);
    const [errors, setErrors] = useState<Record<string, boolean>>({});

    // Original data for display/comparison
    const [originalTag, setOriginalTag] = useState('');

    // Custom Fields State
    const [customFields, setCustomFields] = useState<AssetCustomField[]>([]);
    const [customFieldValues, setCustomFieldValues] = useState<Record<string, any>>({});
    const [initialCustomFields, setInitialCustomFields] = useState<Record<string, any>>({});
    const [isInitialLoad, setIsInitialLoad] = useState(true);

    useEffect(() => {
        const fetchCustomFields = async () => {
            if (!formData.type) {
                setCustomFields([]);
                return;
            }
            try {
                const response = await api.get(`/api/asset-custom-fields/type/${encodeURIComponent(formData.type)}`);
                const activeFields = Array.isArray(response) ? response.filter((f: any) => f.isActive) : [];
                activeFields.sort((a: any, b: any) => a.listOrder - b.listOrder);
                setCustomFields(activeFields);

                // Initialize missing values, prioritizing initial data on first load
                setCustomFieldValues(prev => {
                    // Start with what we have (or what was fetched from asset if this is the initial load)
                    const newValues = { ...(isInitialLoad ? initialCustomFields : prev) };

                    activeFields.forEach((field: any) => {
                        // Only set default if value doesn't exist AND it wasn't in the initial data
                        if (newValues[field.fieldName] === undefined && initialCustomFields[field.fieldName] === undefined) {
                            newValues[field.fieldName] = field.fieldType === 'boolean' ? 'No' : '';
                        }
                    });
                    return newValues;
                });

                // After the first fetch for this type, future changes mean the user changed the type dropdown manually
                setIsInitialLoad(false);
            } catch (err) {
                console.error('Error fetching custom fields:', err);
            }
        };
        fetchCustomFields();
    }, [formData.type, initialCustomFields, isInitialLoad]);

    useEffect(() => {
        const fetchOrganizations = async () => {
            try {
                const orgsRes = await api.get('/api/organization');
                setOrganizations(orgsRes.organizations || []);
            } catch (err) {
                console.error('Error fetching organizations:', err);
            }
        };
        fetchOrganizations();
    }, []);

    useEffect(() => {
        const fetchAsset = async () => {
            try {
                setLoading(true);
                const data = await api.get<Asset>(`/api/assets/${id}`);
                setFormData({
                    name: data.name || '',
                    tag: data.tag || '',
                    type: data.type || '',
                    brand: data.brand || '',
                    model: data.model || '',
                    serialNumber: data.serialNumber || '',
                    description: data.description || '',
                    purchaseDate: data.purchaseDate ? data.purchaseDate.split('T')[0] : '',
                    registrationDate: data.registrationDate ? data.registrationDate.split('T')[0] : '',
                    purchasePrice: data.purchasePrice !== undefined && data.purchasePrice !== null ? data.purchasePrice.toString() : '',
                    warrantyExpiryDate: data.warrantyExpiryDate ? data.warrantyExpiryDate.split('T')[0] : '',
                    status: data.status || 'Available',
                    condition: data.condition || 'Good',
                    location: data.location || '',
                    inspectionCadenceNumber: data.inspectionCadenceNumber !== undefined && data.inspectionCadenceNumber !== null ? data.inspectionCadenceNumber.toString() : '0',
                    inspectionCadenceUnit: data.inspectionCadenceUnit || 'days',
                    addToElectricalRegister: data.addToElectricalRegister || false,
                    electricalCadenceNumber: data.electricalCadenceNumber !== undefined && data.electricalCadenceNumber !== null ? data.electricalCadenceNumber.toString() : '',
                    electricalCadenceUnit: data.electricalCadenceUnit || 'months',
                    addToLiftingRegister: data.addToLiftingRegister || false,
                    liftingCadenceNumber: data.liftingCadenceNumber !== undefined && data.liftingCadenceNumber !== null ? data.liftingCadenceNumber.toString() : '',
                    liftingCadenceUnit: data.liftingCadenceUnit || 'months',
                    organizationId: data.organization?.id ? data.organization.id.toString() : '',
                });
                setOriginalTag(data.tag || '');
                if (data.image) {
                    setCurrentImage(data.image);
                }
                if (data.customFields) {
                    setInitialCustomFields(data.customFields);
                    setCustomFieldValues(data.customFields);
                }
            } catch (err) {
                console.error('Error fetching asset:', err);
                toast.error('Failed to load asset details');
                navigate('/assets');
            } finally {
                setLoading(false);
            }
        };

        if (id) fetchAsset();
    }, [id, navigate]);

    if (!ability.can(ACTIONS.UPDATE, RESOURCES.ASSET)) {
        return <AccessRestricted />;
    }

    const handleInputChange = (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => {
        const { name, value } = e.target;
        setFormData(prev => ({ ...prev, [name]: value }));
        setErrors(prev => ({ ...prev, [name]: false }));
    };

    const handleSelectChange = (name: string, value: string) => {
        setFormData(prev => ({ ...prev, [name]: value as string }));
        setErrors(prev => ({ ...prev, [name]: false }));
    };

    const handleImageChange = (e: React.ChangeEvent<HTMLInputElement>) => {
        const file = e.target.files?.[0];
        if (!file) return;

        // Validate file type
        const allowedTypes = ['image/jpeg', 'image/png', 'image/gif', 'image/webp'];
        if (!allowedTypes.includes(file.type)) {
            toast.error('Invalid file type. Only JPEG, PNG, GIF and WEBP images are allowed.');
            return;
        }

        // Validate file size (10MB)
        if (file.size > 10 * 1024 * 1024) {
            toast.error('File too large. Maximum size is 10MB.');
            return;
        }

        setNewImageFile(file);
        setImageRemoved(false);

        const reader = new FileReader();
        reader.onloadend = () => {
            setImagePreview(reader.result as string);
        };
        reader.readAsDataURL(file);
    };

    const removeImage = () => {
        setNewImageFile(null);
        setImagePreview(null);
        setCurrentImage(null);
        setImageRemoved(true);
    };

    const handleSubmit = async (e: React.FormEvent) => {
        e.preventDefault();

        const validateDate = (dateStr: string) => {
            if (!dateStr) return false;
            const year = dateStr.split('-')[0];
            return year.length === 4;
        };

        const newErrors: Record<string, boolean> = {};
        if (!formData.name) newErrors.name = true;
        if (!formData.tag) newErrors.tag = true;
        if (!formData.type) newErrors.type = true;
        
        if (!formData.purchaseDate || !validateDate(formData.purchaseDate)) {
            newErrors.purchaseDate = true;
            toast.error('Invalid Purchase Date. Year must be 4 digits.');
        }
        
        if (formData.registrationDate && !validateDate(formData.registrationDate)) {
            newErrors.registrationDate = true;
            toast.error('Invalid Registration Date. Year must be 4 digits.');
        }
        
        if (!formData.warrantyExpiryDate || !validateDate(formData.warrantyExpiryDate)) {
            newErrors.warrantyExpiryDate = true;
            toast.error('Invalid Warranty Expiry Date. Year must be 4 digits.');
        }

        if (ability.can(ACTIONS.READ, RESOURCES.ASSET_VALUE)) {
            const price = parseFloat(formData.purchasePrice);
            if (!formData.purchasePrice || isNaN(price) || price <= 0) {
                newErrors.purchasePrice = true;
            }
        }

        // Validate custom fields
        customFields.forEach(field => {
            if (field.isRequired && !customFieldValues[field.fieldName]) {
                newErrors[`custom_${field.fieldName}`] = true;
            }
        });

        if (Object.keys(newErrors).length > 0) {
            setErrors(newErrors);
            toast.error('Please fill in all required fields correctly');
            return;
        }

        try {
            setSaving(true);

            let imageUrl: string | undefined | null = undefined;

            // Upload new image if selected
            if (newImageFile) {
                const imgFormData = new FormData();
                imgFormData.append('image', newImageFile);
                const uploadRes = await api.post('/api/assets/upload', imgFormData, { skipErrorToast: true });
                imageUrl = uploadRes.url;
            } else if (imageRemoved) {
                // If user removed the image without replacing it
                imageUrl = null;
            }

            const payload: any = {
                ...formData,
                purchasePrice: formData.purchasePrice ? parseFloat(formData.purchasePrice) : undefined,
                registrationDate: formData.registrationDate || undefined,
                addToElectricalRegister: formData.addToElectricalRegister,
                electricalCadenceNumber: formData.electricalCadenceNumber ? parseInt(formData.electricalCadenceNumber) : null,
                electricalCadenceUnit: formData.electricalCadenceUnit,
                addToLiftingRegister: formData.addToLiftingRegister,
                liftingCadenceNumber: formData.liftingCadenceNumber ? parseInt(formData.liftingCadenceNumber) : null,
                liftingCadenceUnit: formData.liftingCadenceUnit,
                customFields: customFieldValues,
                organizationId: formData.organizationId ? parseInt(formData.organizationId) : null,
            };

            if (imageUrl !== undefined) {
                payload.image = imageUrl;
            }

            await api.patch(`/api/assets/${id}`, payload, { skipErrorToast: true });
            toast.success('Asset updated successfully');
            navigate('/assets');
        } catch (err: any) {
            console.error('Error updating asset:', err);
            toast.error(err.message || 'Failed to update asset');
        } finally {
            setSaving(false);
        }
    };

    if (loading) {
        return (
            <div className="flex items-center justify-center min-h-[400px]">
                <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600"></div>
            </div>
        );
    }

    const displayImage = imagePreview || (!imageRemoved ? currentImage : null);

    return (
        <div className="max-w-[1200px] mx-auto pb-20 animate-in fade-in slide-in-from-bottom-4 duration-700">
            {/* Header */}
            <div className="flex items-center gap-4 mb-8">
                <Button
                    variant="outline"
                    size="icon"
                    className="rounded-xl border-gray-100 bg-white hover:bg-gray-50 shrink-0"
                    onClick={() => navigate('/assets')}
                >
                    <ChevronLeft size={20} />
                </Button>
                <div>
                    <h1 className="text-2xl font-bold text-gray-900 tracking-tight">Edit Asset: {formData.name}</h1>
                    <p className="text-gray-500 font-medium text-sm">Update information for {originalTag}</p>
                </div>
            </div>

            <div className="grid grid-cols-1 lg:grid-cols-3 gap-8">
                {/* Main Form Area */}
                <div className="lg:col-span-2 space-y-8">
                    {/* Basic Information */}
                    <section className="bg-white p-8 rounded-[32px] border border-gray-100 shadow-sm space-y-6">
                        <div className="flex items-center gap-3 mb-2">
                            <div className="p-2 bg-blue-50 rounded-xl text-blue-600">
                                <Info size={20} />
                            </div>
                            <div>
                                <h3 className="font-bold text-gray-900 text-lg">Basic Information</h3>
                                <p className="text-gray-500 text-sm font-medium">Essential details about the asset</p>
                            </div>
                        </div>

                        <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                            <div className="space-y-2">
                                <label className="text-xs font-bold text-gray-400 uppercase tracking-widest ml-1">Asset Name *</label>
                                <Input
                                    name="name"
                                    value={formData.name}
                                    onChange={handleInputChange}
                                    placeholder="Enter asset name"
                                    className={cn(
                                        "rounded-2xl border-gray-100 bg-gray-50/50 h-12 focus:bg-white transition-all font-medium",
                                        errors.name && "border-red-500 ring-1 ring-red-500"
                                    )}
                                />
                            </div>
                            <div className="space-y-2">
                                <label className="text-xs font-bold text-gray-400 uppercase tracking-widest ml-1">Asset ID/Tag *</label>
                                <Input
                                    name="tag"
                                    value={formData.tag}
                                    onChange={handleInputChange}
                                    placeholder="e.g., VEH-001, IT-001"
                                    className={cn(
                                        "rounded-2xl border-gray-100 bg-gray-50/50 h-12 focus:bg-white transition-all font-medium",
                                        errors.tag && "border-red-500 ring-1 ring-red-500"
                                    )}
                                />
                            </div>
                        </div>

                        <div className="space-y-2">
                            <label className="text-xs font-bold text-gray-400 uppercase tracking-widest ml-1">Organisation</label>
                            <Select value={formData.organizationId} onValueChange={(val) => handleSelectChange('organizationId', val)}>
                                <SelectTrigger className="rounded-2xl border-gray-100 bg-gray-50/50 h-12 focus:bg-white transition-all font-bold">
                                    <SelectValue placeholder="Select Organisation" />
                                </SelectTrigger>
                                <SelectContent className="rounded-2xl border-gray-100">
                                    {organizations.map(org => (
                                        <SelectItem key={org.id} value={org.id.toString()} className="font-bold py-3">{org.companyName}</SelectItem>
                                    ))}
                                </SelectContent>
                            </Select>
                        </div>

                        <div className="space-y-3">
                            <label className="text-xs font-bold text-gray-400 uppercase tracking-widest ml-1">Asset Type *</label>
                            <div className="grid grid-cols-2 sm:grid-cols-3 gap-3">
                                {ASSET_TYPES.map(type => (
                                    <button
                                        key={type.id}
                                        type="button"
                                        onClick={() => {
                                            setFormData(prev => ({ ...prev, type: type.id as AssetType }));
                                            setErrors(prev => ({ ...prev, type: false }));
                                        }}
                                        className={cn(
                                            "flex items-center gap-3 p-4 rounded-2xl border-2 transition-all text-left",
                                            formData.type === type.id
                                                ? "border-blue-600 bg-blue-50/30 text-blue-700 shadow-sm"
                                                : "border-gray-50 bg-gray-50/30 text-gray-500 hover:border-gray-200 hover:bg-gray-100/50",
                                            errors.type && "border-red-500 ring-1 ring-red-500"
                                        )}
                                    >
                                        <div className={cn(
                                            "p-2 rounded-xl shrink-0 transition-colors",
                                            formData.type === type.id
                                                ? "bg-white shadow-sm text-blue-600"
                                                : cn(type.bg, type.color)
                                        )}>
                                            {type.icon}
                                        </div>
                                        <span className={cn(
                                            "font-bold text-sm tracking-tight",
                                            formData.type === type.id ? "text-blue-700" : "text-gray-700"
                                        )}>{type.label}</span>
                                    </button>
                                ))}
                            </div>
                        </div>

                        {/* Type-Specific Details (Custom Fields) */}
                        {customFields.length > 0 && (
                            <div className="pt-4 border-t border-gray-50 space-y-6 animate-in fade-in duration-300">
                                <div className="flex items-center gap-3">
                                    <div className="p-2 bg-indigo-50 rounded-xl text-indigo-600">
                                        <Box size={20} />
                                    </div>
                                    <div>
                                        <h3 className="font-bold text-gray-900 text-base">Type-Specific Details</h3>
                                        <p className="text-gray-500 text-xs font-medium">Additional information for {formData.type}</p>
                                    </div>
                                </div>

                                <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                                    {customFields.map(field => (
                                        <div key={field.id} className="space-y-2">
                                            <label className="text-xs font-bold text-gray-400 uppercase tracking-widest ml-1">
                                                {field.fieldName} {field.isRequired && '*'}
                                            </label>

                                            {field.fieldType === 'text' && (
                                                <Input
                                                    value={customFieldValues[field.fieldName] || ''}
                                                    onChange={(e) => {
                                                        setCustomFieldValues(prev => ({ ...prev, [field.fieldName]: e.target.value }));
                                                        setErrors(prev => ({ ...prev, [`custom_${field.fieldName}`]: false }));
                                                    }}
                                                    placeholder={`Enter ${field.fieldName}`}
                                                    className={cn(
                                                        "rounded-2xl border-gray-100 bg-gray-50/50 h-12 focus:bg-white transition-all font-medium",
                                                        errors[`custom_${field.fieldName}`] && "border-red-500 ring-1 ring-red-500"
                                                    )}
                                                />
                                            )}

                                            {field.fieldType === 'number' && (
                                                <Input
                                                    type="number"
                                                    value={customFieldValues[field.fieldName] || ''}
                                                    onChange={(e) => {
                                                        setCustomFieldValues(prev => ({ ...prev, [field.fieldName]: e.target.value }));
                                                        setErrors(prev => ({ ...prev, [`custom_${field.fieldName}`]: false }));
                                                    }}
                                                    placeholder={`0`}
                                                    className={cn(
                                                        "rounded-2xl border-gray-100 bg-gray-50/50 h-12 focus:bg-white transition-all font-medium",
                                                        errors[`custom_${field.fieldName}`] && "border-red-500 ring-1 ring-red-500"
                                                    )}
                                                />
                                            )}

                                            {field.fieldType === 'select' && field.options && (
                                                <Select
                                                    value={customFieldValues[field.fieldName] || ''}
                                                    onValueChange={(val) => {
                                                        setCustomFieldValues(prev => ({ ...prev, [field.fieldName]: val }));
                                                        setErrors(prev => ({ ...prev, [`custom_${field.fieldName}`]: false }));
                                                    }}
                                                >
                                                    <SelectTrigger className={cn(
                                                        "rounded-2xl border-gray-100 bg-gray-50/50 h-12 focus:bg-white transition-all font-bold",
                                                        errors[`custom_${field.fieldName}`] && "border-red-500 ring-1 ring-red-500"
                                                    )}>
                                                        <SelectValue placeholder={`Select ${field.fieldName}`} />
                                                    </SelectTrigger>
                                                    <SelectContent className="rounded-2xl border-gray-100">
                                                        {field.options.map(opt => (
                                                            <SelectItem key={opt} value={opt} className="font-bold py-3">{opt}</SelectItem>
                                                        ))}
                                                    </SelectContent>
                                                </Select>
                                            )}

                                             {field.fieldType === 'date' && (
                                                <div className="relative">
                                                    <Input
                                                        type="date"
                                                        max="9999-12-31"
                                                        value={customFieldValues[field.fieldName] || ''}
                                                        onChange={(e) => {
                                                            setCustomFieldValues(prev => ({ ...prev, [field.fieldName]: e.target.value }));
                                                            setErrors(prev => ({ ...prev, [`custom_${field.fieldName}`]: false }));
                                                        }}
                                                        className={cn(
                                                            "rounded-2xl border-gray-100 bg-gray-50/50 h-12 focus:bg-white transition-all font-medium pl-4 pr-10",
                                                            errors[`custom_${field.fieldName}`] && "border-red-500 ring-1 ring-red-500"
                                                        )}
                                                    />
                                                </div>
                                            )}

                                            {field.fieldType === 'boolean' && (
                                                <Select
                                                    value={customFieldValues[field.fieldName] || 'No'}
                                                    onValueChange={(val) => {
                                                        setCustomFieldValues(prev => ({ ...prev, [field.fieldName]: val }));
                                                        setErrors(prev => ({ ...prev, [`custom_${field.fieldName}`]: false }));
                                                    }}
                                                >
                                                    <SelectTrigger className={cn(
                                                        "rounded-2xl border-gray-100 bg-gray-50/50 h-12 focus:bg-white transition-all font-bold",
                                                        errors[`custom_${field.fieldName}`] && "border-red-500 ring-1 ring-red-500"
                                                    )}>
                                                        <SelectValue placeholder="Select Yes/No" />
                                                    </SelectTrigger>
                                                    <SelectContent className="rounded-2xl border-gray-100">
                                                        <SelectItem value="Yes" className="font-bold py-3">Yes</SelectItem>
                                                        <SelectItem value="No" className="font-bold py-3">No</SelectItem>
                                                    </SelectContent>
                                                </Select>
                                            )}
                                        </div>
                                    ))}
                                </div>
                            </div>
                        )}

                        <div className="grid grid-cols-1 md:grid-cols-3 gap-6 pt-2">
                            <div className="space-y-2">
                                <label className="text-xs font-bold text-gray-400 uppercase tracking-widest ml-1">Brand</label>
                                <Input
                                    name="brand"
                                    value={formData.brand}
                                    onChange={handleInputChange}
                                    placeholder="e.g., Apple, Dell, Ford"
                                    className="rounded-2xl border-gray-100 bg-gray-50/50 h-12 focus:bg-white transition-all font-medium"
                                />
                            </div>
                            <div className="space-y-2">
                                <label className="text-xs font-bold text-gray-400 uppercase tracking-widest ml-1">Model</label>
                                <Input
                                    name="model"
                                    value={formData.model}
                                    onChange={handleInputChange}
                                    placeholder="e.g., MacBook Pro, Transit"
                                    className="rounded-2xl border-gray-100 bg-gray-50/50 h-12 focus:bg-white transition-all font-medium"
                                />
                            </div>
                            <div className="space-y-2">
                                <label className="text-xs font-bold text-gray-400 uppercase tracking-widest ml-1">Serial Number</label>
                                <Input
                                    name="serialNumber"
                                    value={formData.serialNumber}
                                    onChange={handleInputChange}
                                    placeholder="Enter serial number"
                                    className="rounded-2xl border-gray-100 bg-gray-50/50 h-12 focus:bg-white transition-all font-medium"
                                />
                            </div>
                        </div>

                        <div className="space-y-2">
                            <label className="text-xs font-bold text-gray-400 uppercase tracking-widest ml-1">Description</label>
                            <Textarea
                                name="description"
                                value={formData.description}
                                onChange={handleInputChange}
                                placeholder="Describe the asset and its purpose..."
                                className="rounded-2xl border-gray-100 bg-gray-50/50 min-h-[120px] focus:bg-white transition-all font-medium"
                            />
                        </div>
                    </section>

                    {/* Purchase & Warranty */}
                    <section className="bg-white p-8 rounded-[32px] border border-gray-100 shadow-sm space-y-6">
                        <div className="flex items-center gap-3 mb-2">
                            <div className="p-2 bg-green-50 rounded-xl text-green-600">
                                <DollarSign size={20} />
                            </div>
                            <div>
                                <h3 className="font-bold text-gray-900 text-lg">Purchase & Warranty</h3>
                                <p className="text-gray-500 text-sm font-medium">Financial and warranty information</p>
                            </div>
                        </div>

                        <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                            <div className="space-y-2">
                                <label className="text-xs font-bold text-gray-400 uppercase tracking-widest ml-1">Purchase Date *</label>
                                <div className="relative">
                                     <Input
                                        type="date"
                                        name="purchaseDate"
                                        max="9999-12-31"
                                        value={formData.purchaseDate}
                                        onChange={handleInputChange}
                                        className={cn(
                                            "rounded-2xl border-gray-100 bg-gray-50/50 h-12 focus:bg-white transition-all font-medium pl-4 pr-10",
                                            errors.purchaseDate && "border-red-500 ring-1 ring-red-500"
                                        )}
                                    />
                                </div>
                            </div>
                            <div className="space-y-2">
                                <label className="text-xs font-bold text-gray-400 uppercase tracking-widest ml-1">Registration Date</label>
                                <div className="relative">
                                     <Input
                                        type="date"
                                        name="registrationDate"
                                        max="9999-12-31"
                                        value={formData.registrationDate}
                                        onChange={handleInputChange}
                                        className={cn(
                                            "rounded-2xl border-gray-100 bg-gray-50/50 h-12 focus:bg-white transition-all font-medium pl-4 pr-10",
                                            errors.registrationDate && "border-red-500 ring-1 ring-red-500"
                                        )}
                                    />
                                </div>
                            </div>
                            {ability.can(ACTIONS.READ, RESOURCES.ASSET_VALUE) && (
                                <div className="space-y-2">
                                    <label className="text-xs font-bold text-gray-400 uppercase tracking-widest ml-1">Purchase Price *</label>
                                    <div className="relative group">
                                        <div className="absolute left-4 top-1/2 -translate-y-1/2 text-gray-400 font-bold">$</div>
                                        <Input
                                            name="purchasePrice"
                                            type="number"
                                            step="0.01"
                                            value={formData.purchasePrice}
                                            onChange={handleInputChange}
                                            placeholder="0.00"
                                            className={cn(
                                                "rounded-2xl border-gray-100 bg-gray-50/50 h-12 focus:bg-white transition-all font-medium pl-8",
                                                errors.purchasePrice && "border-red-500 ring-1 ring-red-500"
                                            )}
                                        />
                                    </div>
                                </div>
                            )}
                            <div className="space-y-2">
                                <label className="text-xs font-bold text-gray-400 uppercase tracking-widest ml-1">Warranty Expiry Date *</label>
                                 <Input
                                    type="date"
                                    name="warrantyExpiryDate"
                                    max="9999-12-31"
                                    value={formData.warrantyExpiryDate}
                                    onChange={handleInputChange}
                                    className={cn(
                                        "rounded-2xl border-gray-100 bg-gray-50/50 h-12 focus:bg-white transition-all font-medium",
                                        errors.warrantyExpiryDate && "border-red-500 ring-1 ring-red-500"
                                    )}
                                />
                            </div>
                        </div>
                    </section>

                    {/* Status & Location */}
                    <section className="bg-white p-8 rounded-[32px] border border-gray-100 shadow-sm space-y-6">
                        <div className="flex items-center gap-3 mb-2">
                            <div className="p-2 bg-orange-50 rounded-xl text-orange-600">
                                <AlertCircle size={20} />
                            </div>
                            <div>
                                <h3 className="font-bold text-gray-900 text-lg">Status & Location</h3>
                                <p className="text-gray-500 text-sm font-medium">Current status and physical location</p>
                            </div>
                        </div>

                        <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                            <div className="space-y-2 col-span-1 md:col-span-2">
                                <label className="text-xs font-bold text-gray-400 uppercase tracking-widest ml-1">Condition</label>
                                <Select value={formData.condition} onValueChange={(val) => handleSelectChange('condition', val)}>
                                    <SelectTrigger className="rounded-2xl border-gray-100 bg-gray-50/50 h-12 focus:bg-white transition-all font-bold">
                                        <SelectValue placeholder="Select Condition" />
                                    </SelectTrigger>
                                    <SelectContent className="rounded-2xl border-gray-100">
                                        {CONDITION_OPTIONS.map(opt => (
                                            <SelectItem key={opt} value={opt} className="font-bold py-3">{opt}</SelectItem>
                                        ))}
                                    </SelectContent>
                                </Select>
                            </div>
                        </div>

                        <div className="space-y-2">
                            <label className="text-xs font-bold text-gray-400 uppercase tracking-widest ml-1">Location</label>
                            <Input
                                name="location"
                                value={formData.location}
                                onChange={handleInputChange}
                                placeholder="e.g., Warehouse A, Office Floor 2"
                                className="rounded-2xl border-gray-100 bg-gray-50/50 h-12 focus:bg-white transition-all font-medium"
                            />
                        </div>
                    </section>

                    {/* Inspection Cadence */}
                    <section className="bg-white p-8 rounded-[32px] border border-gray-100 shadow-sm space-y-6">
                        <div className="flex items-center gap-3 mb-2">
                            <div className="p-2 bg-blue-50 rounded-xl text-blue-600">
                                <CheckCircle2 size={20} />
                            </div>
                            <div>
                                <h3 className="font-bold text-gray-900 text-lg">Inspection Settings</h3>
                                <p className="text-gray-500 text-sm font-medium">Set the cadence for routine inspections</p>
                            </div>
                        </div>

                        <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                            <div className="space-y-2">
                                <label className="text-xs font-bold text-gray-400 uppercase tracking-widest ml-1">Inspection Cadence Frequency</label>
                                <Input
                                    name="inspectionCadenceNumber"
                                    type="number"
                                    min="0"
                                    value={formData.inspectionCadenceNumber}
                                    onChange={handleInputChange}
                                    placeholder="e.g. 90, 6, 12"
                                    className="rounded-2xl border-gray-100 bg-gray-50/50 h-12 focus:bg-white transition-all font-medium"
                                />
                            </div>
                            <div className="space-y-2">
                                <label className="text-xs font-bold text-gray-400 uppercase tracking-widest ml-1">Time Unit</label>
                                <Select value={formData.inspectionCadenceUnit} onValueChange={(val) => handleSelectChange('inspectionCadenceUnit', val)}>
                                    <SelectTrigger className="rounded-2xl border-gray-100 bg-gray-50/50 h-12 focus:bg-white transition-all font-bold">
                                        <SelectValue placeholder="Select Unit" />
                                    </SelectTrigger>
                                    <SelectContent className="rounded-2xl border-gray-100">
                                        <SelectItem value="days" className="font-bold py-3">Days</SelectItem>
                                        <SelectItem value="weeks" className="font-bold py-3">Weeks</SelectItem>
                                        <SelectItem value="months" className="font-bold py-3">Months</SelectItem>
                                    </SelectContent>
                                </Select>
                            </div>
                        </div>
                    </section>

                    {/* Electrical Register */}
                    <section className="bg-white p-8 rounded-[32px] border border-gray-100 shadow-sm space-y-6">
                        <div className="flex items-center gap-3 mb-2">
                            <div className="p-2 bg-yellow-50 rounded-xl text-yellow-600">
                                <Info size={20} />
                            </div>
                            <div>
                                <h3 className="font-bold text-gray-900 text-lg">Electrical Equipment Register</h3>
                                <p className="text-gray-500 text-sm font-medium">Test & Tag compliance settings</p>
                            </div>
                        </div>

                        <div className="space-y-6">
                            <div className="flex items-center justify-between p-4 bg-gray-50 rounded-2xl border border-gray-100">
                                <div className="space-y-0.5">
                                    <label className="text-sm font-bold text-gray-900 tracking-tight cursor-pointer" htmlFor="electrical-register">
                                        Add to Electrical Equipment Register
                                    </label>
                                    <p className="text-xs text-gray-500 font-medium">
                                        Enable this for electrical tools/equipment that require routine Test & Tag.
                                    </p>
                                </div>
                                <Switch 
                                    id="electrical-register"
                                    checked={formData.addToElectricalRegister}
                                    onCheckedChange={(checked) => setFormData(prev => ({ ...prev, addToElectricalRegister: checked }))}
                                />
                            </div>

                            {formData.addToElectricalRegister && (
                                <div className="grid grid-cols-1 md:grid-cols-2 gap-6 animate-in slide-in-from-top-2 duration-300">
                                    <div className="space-y-2">
                                        <label className="text-xs font-bold text-gray-400 uppercase tracking-widest ml-1">Per-Asset Cadence Override</label>
                                        <Input
                                            name="electricalCadenceNumber"
                                            type="number"
                                            min="1"
                                            value={formData.electricalCadenceNumber}
                                            onChange={handleInputChange}
                                            placeholder="Defaults to global setting"
                                            className="rounded-2xl border-gray-100 bg-gray-50/50 h-12 focus:bg-white transition-all font-medium"
                                        />
                                        <p className="text-[10px] text-gray-400 font-medium ml-1">
                                            Leave blank to use the global default cadence.
                                        </p>
                                    </div>
                                    <div className="space-y-2">
                                        <label className="text-xs font-bold text-gray-400 uppercase tracking-widest ml-1">Time Unit</label>
                                        <Select value={formData.electricalCadenceUnit} onValueChange={(val) => handleSelectChange('electricalCadenceUnit', val)}>
                                            <SelectTrigger className="rounded-2xl border-gray-100 bg-gray-50/50 h-12 focus:bg-white transition-all font-bold">
                                                <SelectValue placeholder="Select Unit" />
                                            </SelectTrigger>
                                            <SelectContent className="rounded-2xl border-gray-100">
                                                <SelectItem value="days" className="font-bold py-3">Days</SelectItem>
                                                <SelectItem value="weeks" className="font-bold py-3">Weeks</SelectItem>
                                                <SelectItem value="months" className="font-bold py-3">Months</SelectItem>
                                            </SelectContent>
                                        </Select>
                                    </div>
                                </div>
                            )}
                        </div>
                    </section>

                    {/* Lifting Register */}
                    <section className="bg-white p-8 rounded-[32px] border border-gray-100 shadow-sm space-y-6">
                        <div className="flex items-center gap-3 mb-2">
                            <div className="p-2 bg-orange-50 rounded-xl text-orange-600">
                                <Anchor size={20} />
                            </div>
                            <div>
                                <h3 className="font-bold text-gray-900 text-lg">Lifting Equipment Register</h3>
                                <p className="text-gray-500 text-sm font-medium">Lifting gear and sling compliance settings</p>
                            </div>
                        </div>

                        <div className="space-y-6">
                            <div className="flex items-center justify-between p-4 bg-gray-50 rounded-2xl border border-gray-100">
                                <div className="space-y-0.5">
                                    <label className="text-sm font-bold text-gray-900 tracking-tight cursor-pointer" htmlFor="lifting-register">
                                        Add to Lifting Equipment Register
                                    </label>
                                    <p className="text-xs text-gray-500 font-medium">
                                        Enable this for lifting gear, slings, and shackles that require routine inspection.
                                    </p>
                                </div>
                                <Switch
                                    id="lifting-register"
                                    checked={formData.addToLiftingRegister}
                                    onCheckedChange={(checked) => setFormData(prev => ({ ...prev, addToLiftingRegister: checked }))}
                                />
                            </div>

                            {formData.addToLiftingRegister && (
                                <div className="grid grid-cols-1 md:grid-cols-2 gap-6 animate-in slide-in-from-top-2 duration-300">
                                    <div className="space-y-2">
                                        <label className="text-xs font-bold text-gray-400 uppercase tracking-widest ml-1">Per-Asset Cadence Override</label>
                                        <Input
                                            name="liftingCadenceNumber"
                                            type="number"
                                            min="1"
                                            value={formData.liftingCadenceNumber}
                                            onChange={handleInputChange}
                                            placeholder="Defaults to global setting"
                                            className="rounded-2xl border-gray-100 bg-gray-50/50 h-12 focus:bg-white transition-all font-medium"
                                        />
                                        <p className="text-[10px] text-gray-400 font-medium ml-1">
                                            Leave blank to use the global default cadence.
                                        </p>
                                    </div>
                                    <div className="space-y-2">
                                        <label className="text-xs font-bold text-gray-400 uppercase tracking-widest ml-1">Time Unit</label>
                                        <Select value={formData.liftingCadenceUnit} onValueChange={(val) => handleSelectChange('liftingCadenceUnit', val)}>
                                            <SelectTrigger className="rounded-2xl border-gray-100 bg-gray-50/50 h-12 focus:bg-white transition-all font-bold">
                                                <SelectValue placeholder="Select Unit" />
                                            </SelectTrigger>
                                            <SelectContent className="rounded-2xl border-gray-100">
                                                <SelectItem value="days" className="font-bold py-3">Days</SelectItem>
                                                <SelectItem value="weeks" className="font-bold py-3">Weeks</SelectItem>
                                                <SelectItem value="months" className="font-bold py-3">Months</SelectItem>
                                            </SelectContent>
                                        </Select>
                                    </div>
                                </div>
                            )}
                        </div>
                    </section>

                    {/* Asset Image */}
                    <section className="bg-white p-8 rounded-[32px] border border-gray-100 shadow-sm space-y-6">
                        <div className="flex items-center gap-3 mb-2">
                            <div className="p-2 bg-slate-50 rounded-xl text-slate-600">
                                <ImageIcon size={20} />
                            </div>
                            <div>
                                <h3 className="font-bold text-gray-900 text-lg">Asset Photo</h3>
                                <p className="text-gray-500 text-sm font-medium">Upload or change the photo for this asset</p>
                            </div>
                        </div>

                        {!displayImage ? (
                            <div
                                className="border-2 border-dashed border-gray-100 rounded-[28px] p-12 text-center hover:border-blue-300 hover:bg-blue-50/30 transition-all cursor-pointer group"
                                onClick={() => document.getElementById('image-upload')?.click()}
                            >
                                <div className="w-16 h-16 bg-gray-50 rounded-2xl flex items-center justify-center mx-auto mb-4 group-hover:scale-110 transition-transform">
                                    <Upload size={24} className="text-gray-400 group-hover:text-blue-500" />
                                </div>
                                <p className="font-bold text-gray-900">Click to upload image</p>
                                <p className="text-gray-400 text-[10px] mt-1 uppercase tracking-widest font-bold">JPG, PNG, GIF or WEBP • MAX 10MB</p>
                                <input
                                    id="image-upload"
                                    type="file"
                                    accept="image/*"
                                    className="hidden"
                                    onChange={handleImageChange}
                                />
                            </div>
                        ) : (
                            <div className="relative w-full aspect-video rounded-[28px] overflow-hidden border border-gray-100 shadow-inner group">
                                <img src={displayImage} alt="Preview" className="w-full h-full object-cover" />
                                <div className="absolute inset-0 bg-black/40 opacity-0 group-hover:opacity-100 transition-opacity flex items-center justify-center gap-3">
                                    <Button
                                        type="button"
                                        variant="destructive"
                                        className="rounded-xl h-10 px-4 font-bold gap-2"
                                        onClick={removeImage}
                                    >
                                        <Trash2 size={18} />
                                        Remove Photo
                                    </Button>
                                    <Button
                                        type="button"
                                        variant="secondary"
                                        className="rounded-xl h-10 px-4 font-bold gap-2 bg-white text-gray-900 hover:bg-gray-50"
                                        onClick={() => document.getElementById('image-upload')?.click()}
                                    >
                                        <Upload size={18} />
                                        Replace
                                    </Button>
                                    <input
                                        id="image-upload"
                                        type="file"
                                        accept="image/*"
                                        className="hidden"
                                        onChange={handleImageChange}
                                    />
                                </div>
                            </div>
                        )}

                        {newImageFile && (
                            <div className="mt-4 flex items-center gap-3 px-4 py-3 bg-blue-50 rounded-2xl animate-in fade-in slide-in-from-bottom-2 duration-300">
                                <div className="w-2 h-2 rounded-full bg-blue-500 animate-pulse" />
                                <span className="text-xs font-bold text-blue-700">
                                    New photo selected: {newImageFile.name}
                                </span>
                                <span className="text-[10px] font-bold text-blue-400 uppercase tracking-wider ml-auto">
                                    {(newImageFile.size / (1024 * 1024)).toFixed(2)} MB
                                </span>
                            </div>
                        )}
                    </section>
                </div>

                {/* Right Sidebar - Sticky Summary */}
                <div className="lg:col-span-1">
                    <div className="sticky top-24 space-y-6">
                        <div className="bg-white p-8 rounded-[32px] border border-gray-100 shadow-xl shadow-gray-200/50 space-y-6">
                            <h3 className="font-bold text-gray-900 uppercase tracking-widest text-xs flex items-center gap-2">
                                <LayoutGrid size={16} className="text-blue-500" />
                                Edit Summary
                            </h3>

                            <div className="space-y-4">
                                <div className="p-4 bg-gray-50 rounded-2xl space-y-3">
                                    <div className="flex justify-between items-start">
                                        <span className="text-[10px] font-bold text-gray-400 uppercase tracking-wider">Name</span>
                                        <span className={cn("text-xs font-bold text-right ml-2", formData.name ? "text-gray-900" : "text-gray-300")}>
                                            {formData.name || 'Not set'}
                                        </span>
                                    </div>
                                    <div className="flex justify-between items-start">
                                        <span className="text-[10px] font-bold text-gray-400 uppercase tracking-wider">Type</span>
                                        <span className={cn("text-xs font-bold text-right ml-2", formData.type ? "text-blue-600" : "text-gray-300")}>
                                            {formData.type || 'Not selected'}
                                        </span>
                                    </div>
                                    <div className="flex justify-between items-start">
                                        <span className="text-[10px] font-bold text-gray-400 uppercase tracking-wider">Status</span>
                                        <Badge className="bg-blue-50 text-blue-600 border-0 text-[10px] font-bold px-2 py-0.5 rounded-lg text-right ml-2">
                                            {formData.status}
                                        </Badge>
                                    </div>
                                </div>

                                <div className="space-y-3 pt-2">
                                    <Button
                                        className="w-full bg-slate-950 hover:bg-slate-900 text-white rounded-2xl h-14 font-bold shadow-lg shadow-slate-950/20 transition-all flex items-center justify-center gap-3 disabled:opacity-50"
                                        onClick={handleSubmit}
                                        disabled={saving}
                                    >
                                        {saving ? (
                                            <div className="w-5 h-5 border-2 border-white/20 border-t-white rounded-full animate-spin" />
                                        ) : (
                                            <Save size={20} />
                                        )}
                                        {saving ? 'Saving...' : 'Save Changes'}
                                    </Button>

                                    <div className="space-y-2 px-1">
                                        <div className="flex items-start gap-2 text-[10px] font-bold text-gray-400 uppercase tracking-wide">
                                            <CheckCircle2 size={12} className="text-green-500 shrink-0 mt-0.5" />
                                            Changes will be saved immediately
                                        </div>
                                        <div className="flex items-start gap-2 text-[10px] font-bold text-gray-400 uppercase tracking-wide">
                                            <CheckCircle2 size={12} className="text-green-500 shrink-0 mt-0.5" />
                                            Activity will be logged
                                        </div>
                                    </div>
                                </div>
                            </div>
                        </div>
                    </div>
                </div>
            </div>
        </div>
    );
};

export default EditAsset;
